import { useNavigate, useSearch } from '@tanstack/react-router';

import { ContractState } from '../../../types/ContractMessage';
import { Button } from '../../cdl/Button/Button';
import { useDrawer } from '../../cdl/Drawer/useDrawer.hook';
import { RadioCardGroup } from '../../cdl/RadioCard/RadioCardGroup';
import { Text } from '../../cdl/Text/Text';
import { translate } from '../../common/helpers/translate.helper';
import { useDocumentTitle } from '../../common/hooks/useDocumentTitle';
import { useRole } from '../../common/hooks/useRole';
import { IconPlus } from '../../common/icons/cdl/Plus';
import { Page } from '../../common/Page/Page';
import { PageGrid } from '../../common/PageGrid';
import { Box } from '../../common/ui/Box';
import { CreateContractDrawer } from '../../contracts/form/CreateContractDrawer';
import { ContractsFilterPills } from '../../contracts/overview/components/ContractsFilterPills';
import { ContractsTable } from '../../contracts/overview/components/ContractsTable';
import {
    ContractQuickFilter,
    contractQuickFilterToState,
    contractStatesToQuickFilter,
} from '../../contracts/overview/contractQuickFilterHelper';

import { ContractStatusTag } from './components/ContractStatusTag';

const quickFilterLabelByValue: Record<ContractQuickFilter, string> = {
    [ContractQuickFilter.ALL]: translate('contextSwitch.all.title'),
    [ContractQuickFilter.DRAFT]: translate('contextSwitch.draft.title'),
    [ContractQuickFilter.AWARDED]: translate('contextSwitch.awarded.title'),
    [ContractQuickFilter.ACTIVE]: translate('contextSwitch.active.title'),
    [ContractQuickFilter.INACTIVE]: translate('contextSwitch.inactive.title'),
};

const quickFilterOptions = Object.values(ContractQuickFilter).map((value) => ({
    value,
    primaryLabel: quickFilterLabelByValue[value],
}));

export const ContractsOverviewPage = () => {
    useDocumentTitle(translate('contracts.pageTitle'));

    const role = useRole();
    const { push, pop } = useDrawer();
    const search = useSearch({ from: '/_app/contracts/' });
    const navigate = useNavigate({ from: '/contracts' });

    const selectedQuickFilter = contractStatesToQuickFilter(search.states);

    const handleQuickFilterChange = (quickFilter: ContractQuickFilter) =>
        navigate({
            search: (previousSearch) => ({
                ...previousSearch,
                states: contractQuickFilterToState[quickFilter] ?? [],
                page: 0,
            }),
        });

    const openCreateDrawer = () => {
        const drawerId = push({
            header: {
                title: translate('contracts.form.createTitle'),
                trailingVisual: <ContractStatusTag status={ContractState.DRAFT} />,
            },
            content: (
                <CreateContractDrawer
                    onSuccess={(contract) => {
                        pop(drawerId);

                        if (contract.state !== ContractState.DRAFT) {
                            navigate({ to: '/contracts/$id', params: { id: contract.id } });
                        }
                    }}
                />
            ),
        });
    };

    return (
        <Page>
            <PageGrid>
                <Box display="flex" justifyContent="space-between" alignItems="center" gap={16}>
                    <Text variant="headline" as="h1">
                        {translate('contracts.headline')}
                    </Text>

                    {role.hasAnyAdminRights() ? (
                        <Button emphasis="high" LeadingIcon={IconPlus} onClick={openCreateDrawer}>
                            {translate('contracts.form.createAction')}
                        </Button>
                    ) : null}
                </Box>
                <RadioCardGroup<ContractQuickFilter>
                    options={quickFilterOptions}
                    onChange={handleQuickFilterChange}
                    value={selectedQuickFilter}
                />
                <ContractsFilterPills />
                <ContractsTable />
            </PageGrid>
        </Page>
    );
};
